"use client";

import { UserRole } from "@/common/types";
import { RootState } from "@/store/store";
import { usePathname, useRouter } from "next/navigation";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isTokenExpired } from "@/common/utils/isTokenExpiared";
import { signOutThunk } from "@/features/auth/authSlice";
import type { AppDispatch } from "@/store/store";

interface ProtectedRouteProps {
  children: ReactNode;
}

const PUBLIC_PATHS = ["/terms", "/"];

// Try to mint a new access token using the httpOnly refresh cookie.
// Resolves to the new token on success, or null if the refresh cookie
// is missing/expired.
async function tryRefreshAccessToken(): Promise<string | null> {
  try {
    const res = await fetch("/api/graphql", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: `mutation RefreshTokens { refreshTokens { accessToken } }`,
      }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    const token: string | undefined = json?.data?.refreshTokens?.accessToken;
    if (!token) return null;
    localStorage.setItem("accessToken", token);
    return token;
  } catch {
    return null;
  }
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const user = useSelector((state: RootState) => state.auth.user);
  const sessionLost = useSelector((state: RootState) => state.auth.sessionLost);
  const pathname = usePathname();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const [refreshing, setRefreshing] = useState(false);
  const checkingRef = useRef(false);

  const isPublicPath = !!pathname && (
    pathname.startsWith("/auth") ||
    PUBLIC_PATHS.some((path) => pathname === path)
  );

  // Synchronously decide whether the session needs revalidation and, if so,
  // unmount children while the refresh is in flight. Unmounting first is the
  // whole point — any active queries on the page would otherwise refetch on
  // resume and render their failure UI before our sign-out finishes.
  const verifySession = useCallback(async () => {
    if (isPublicPath) return;
    if (checkingRef.current) return;
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken && !isTokenExpired(accessToken)) return;

    checkingRef.current = true;
    setRefreshing(true);
    try {
      const token = await tryRefreshAccessToken();
      if (!token) {
        await dispatch(signOutThunk());
        router.replace("/auth");
        return;
      }
    } finally {
      checkingRef.current = false;
      setRefreshing(false);
    }
  }, [isPublicPath, dispatch, router]);

  useEffect(() => {
    if (!pathname) return;

    const accessToken = localStorage.getItem("accessToken");
    const needsRefresh =
      !isPublicPath && (!accessToken || isTokenExpired(accessToken));

    if (needsRefresh) {
      void verifySession();
      return;
    }

    if (!user || !user.role) return;

    const userRoleName = user?.role;
    switch (userRoleName) {
      case UserRole.PROJECT_MANAGER:
        if (pathname.startsWith("/scope") || pathname.startsWith("/review")) {
          router.replace("/pm/dashboard");
        }
        break;

      case UserRole.FOREMAN:
        if (!isForemanAllowedPath(pathname)) {
          router.replace("/foreman/schedule");
        }
        break;

      case UserRole.WORKER:
        if (!isWorkerAllowedPath(pathname)) {
          router.replace("/dashboard");
        }
        break;

      default:
        const isAllowed = UNAUTHORIZED_ALLOWED_PATHS.some((path) =>
          pathname.includes(path),
        );

        if (!isAllowed) {
          // If role is unknown or missing, redirect to auth
          router.replace("/auth");
        }
        break;
    }
  }, [user, pathname, router, dispatch, isPublicPath, verifySession]);

  // Re-verify the session whenever the app comes back to the foreground.
  // Children are unmounted via `refreshing` while the check runs, so any
  // queries on the page can't fire (and can't show error UI) until we've
  // confirmed the session is still valid.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onVisibility = () => {
      if (document.visibilityState === "visible") void verifySession();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void verifySession();
    };
    const onFocus = () => void verifySession();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
    };
  }, [verifySession]);

  // sessionLost is set synchronously by Apollo's errorLink the instant any
  // query/mutation returns UNAUTHENTICATED. Unmounting children here in the
  // same React tick prevents the failing query's error UI from rendering
  // before handleSessionLost() finishes the redirect to /auth.
  if (refreshing || (sessionLost && !isPublicPath)) return null;
  return <>{children}</>;
}
const UNAUTHORIZED_ALLOWED_PATHS = ["/signup", "/auth", "/terms"];

function isForemanAllowedPath(pathname: string) {
  if (pathname.startsWith("/foreman")) return true;
  if (pathname.startsWith("/auth")) return true;
  const PUBLIC = ["/", "/terms"];
  return PUBLIC.includes(pathname);
}

function isWorkerAllowedPath(pathname: string) {
  const ALLOWED_PATHS_FOR_WORKER = [
    "/",
    "/home",
    "/dashboard",
    "/map",
    "/scope",
    "/scope/hazard",
    "/review",
  ];

  if (pathname.startsWith("/worker")) return true;

  const ALLOWED_PROJECT_SUBPATHS = ["", "calendar", "documents", "forms"];
  const PROJECT_PATH_FOR_WORKER_REGEX = new RegExp(
    `^/project/(?!new$)[a-zA-Z0-9-]+(/(${ALLOWED_PROJECT_SUBPATHS.join("|")}))?$`,
  );
  const PROJECT_FORMS_REGEX = /^\/project\/[a-zA-Z0-9-]+\/forms(\/.*)?$/;

  if (ALLOWED_PATHS_FOR_WORKER.includes(pathname)) return true;

  if (pathname.startsWith("/auth")) return true;

  if (PROJECT_PATH_FOR_WORKER_REGEX.test(pathname)) return true;
  if (PROJECT_FORMS_REGEX.test(pathname)) return true;

  return false;
}
